import { defineStore } from 'pinia'
import { SessionReply } from '@Utils/sessionReply'
import { spmReport } from '@Utils/report'
import QuestionnaireSdk from '@bilibili-business/questionnaire-sdk'
import { ACCOUNT_STATUS_ENUM } from '@Constant/authStatus'
import { BASE_URL } from '../configs/index'
import { UpstreamUserInfoVo } from '@/swagger/interface'
import { set } from 'lodash'

interface GlobalState {
  baseUrl: string
  accountInfo: UpstreamUserInfoVo
  routerMaps: any[]
  freshCart: boolean
  riskMsg: any
  showGuideList: string[]
  showAgreementType: number
  vvTransVt: boolean
  inviteGoodsLimit: {
    commentGoodsLimit: number
    commentLinkGoodsLimit: number
  }
  logoGuideState: number
  loginGuideState: number
  createOrderUpperList: any[]
  huahuoPlusConfig: any
  finishProtocol: boolean
}

export const useGlobalStore = defineStore({
  id: 'global',
  state: (): GlobalState => ({
    baseUrl: BASE_URL,
    accountInfo: {} as UpstreamUserInfoVo,
    routerMaps: [],
    freshCart: false,
    riskMsg: {},
    showGuideList: [], // 不互相冲突的引导提示——如红点，局部页面的新手引导
    showAgreementType: -1, // 需要展示的全局提示：签署协议弹框、全局的新手引导
    vvTransVt: false,
    inviteGoodsLimit: {
      commentGoodsLimit: 5, // 商品
      commentLinkGoodsLimit: 4 // 商品+落地页
    },
    logoGuideState: 0,
    loginGuideState: 0, // login 全局新手引导提示状态
    createOrderUpperList: [],
    huahuoPlusConfig: {},
    finishProtocol: false
  }),
  getters: {
    role: state => state.accountInfo && state.accountInfo.account_role, // 1-品牌主 2-代理商
    accountId: state => state.accountInfo && state.accountInfo.account_id,
    mid: state => state.accountInfo && state.accountInfo.mid,
    isAgent: state => state.accountInfo.account_role === 2, // 是代理商
    showMatexReport: state => state.accountInfo.is_matex === 1, // 命中matex投后白名单
    showSpreadDepthReport: state => state.accountInfo.lable_right && state.accountInfo.lable_right.postSpreadDepth, // 显示传播深度
    isFirstAgent: state => state.accountInfo.account_role === 2 && state.accountInfo.is_second_agent === 0, // 是否是一级代理商
    isFirstCoreAgent: state => state.accountInfo.account_role === 2 && state.accountInfo.is_core_agent === 1 && state.accountInfo.is_second_agent === 0, // 一级核代
    isSecondCoreAgent: state => state.accountInfo.account_role === 2 && state.accountInfo.parent_is_core_agent === 1, // 二级核代
    isSecondAgent: state => state.accountInfo.is_second_agent === 1, // 是否是二级代理商
    secondaryAgentAuth: state => state.accountInfo.secondary_agent_auth || {}, // 二级代理商的权限
    isFullCoreAccount: state => (state.accountInfo.is_second_agent === 1 ? state.accountInfo.parent_is_core_agent === 1 : state.accountInfo.is_core_agent === 1),
    departmentType: state => state.accountInfo && state.accountInfo.department_type,
    showMsg: state => state.riskMsg.showMsg, // 全局风险提示
    riskMsgContent: state => state.riskMsg.riskMsgContent, // 全局风险提示内容
    accountQualificationValid: state => [ACCOUNT_STATUS_ENUM.CERTIFIED, ACCOUNT_STATUS_ENUM.WILL_EXPIRE, ACCOUNT_STATUS_ENUM.EXPIRED].includes(state.accountInfo.account_audit_status), // 账号资质有效
    liveSquareAuth: state => !(state.accountInfo.is_jd_union_account || state.accountInfo.is_taolian_spark), // 直播广场权限：非京东联盟账号、非淘联账号
    orderAuthState: state => {
      // 子账号商单授权权限状态
      if (state.accountInfo.is_second_agent === 1 && !state.accountInfo.secondary_agent_auth?.order_auth_state) {
        return false
      }
      return true
    }
  },

  actions: {
    setAccountInfo(payload: UpstreamUserInfoVo = {}) {
      const { account_id: accountId = '', account_name: accountName = '' } = payload

      this.accountInfo = payload
      SessionReply.setAccountId(accountId)
      spmReport.setAccountInfo(payload)
      QuestionnaireSdk.setUser(accountId as unknown as any)
    },
    setRouterMaps(payload = {}) {
      this.routerMaps = payload
    },
    setFreshCart(payload = false) {
      this.freshCart = payload
    },
    setFirstContract(payload = false) {
      this.firstContract = payload
    },
    setRiskMsg(payload = { showMsg: false, riskMsgContent: [] }) {
      this.riskMsg = payload
    },
    setShowGuideList(payload = []) {
      this.showGuideList = payload
    },
    setShowAgreementType(payload) {
      this.showAgreementType = payload
    },
    setVvTransVt(payload) {
      this.vvTransVt = payload
    },
    setInviteGoodsLimit(payload) {
      this.inviteGoodsLimit = payload
    },
    setLoginGuideState(payload) {
      this.loginGuideState = payload
    },
    setLogoGuideState(payload) {
      this.logoGuideState = payload
    },
    setCreateOrderUpperList(payload) {
      this.createOrderUpperList = payload
    },
    setHuahuoPlusConfig(payload) {
      this.huahuoPlusConfig = payload
    },
    setFinishProtocol(payload) {
      this.finishProtocol = payload
    }
  }
})
